/**
 * Settings → Security → App passwords (design https://claude.ai/artifact/U8HvUzHE2tpDgcWQjjbQAa): one app password
 * per device or app, like Google's, for IMAP, SMTP, CalDAV and CardDAV. Lists mine in this workspace (name, created,
 * last used), adds one through the existing setups (a profile for an iPhone, iPad or Mac; a mail app; a calendar or
 * contacts app), each named first, shows a new password once, and revokes one or all after a confirmation. Making
 * one never signs the others out. Online only.
 */
import type { AppPassword } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { errorKind } from "../ports.ts";
import type { SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** The server's cap per person per workspace (apps/api routes/calendar.ts MAX_APP_PASSWORDS). */
export const APP_PASSWORD_LIMIT = 50;
/** From this many on, the list says how close the limit is. */
export const APP_PASSWORD_NEAR_LIMIT = 45;
/** A password nobody signed in with for this long gets a "not used in a while" hint. */
export const APP_PASSWORD_STALE_MS = 90 * 86_400_000;

/** Which existing setup Add device opens. */
export type AppPasswordSetup = "profile" | "mail" | "calendar";

export type AppPasswordsState = {
  status: "loading" | "ready" | "error";
  /** Newest first. */
  passwords: AppPassword[];
  /** At the limit: Add device is off and a banner says why. */
  atLimit: boolean;
  /** Close to it: the banner counts. */
  nearLimit: boolean;
  /** The add sheet: naming, then (mail and calendar) the password shown once. */
  adding?: {
    setup: AppPasswordSetup;
    name: string;
    busy: boolean;
    /** Made: what the app asks for. The password isn't kept anywhere else. */
    made?: { password: string; username: string; server: string; label: string };
  };
  /** Asking "Revoke …?" for one password, or all of them. */
  confirming?: { kind: "one"; id: string; label: string | null } | { kind: "all"; count: number };
  revoking: boolean;
  notice?: MessageDescriptor;
};

/** A profile to save to the device (the platform downloads it). */
export type AppleProfileFile = { filename: string; profile: string; signed: boolean };

function failure(e: unknown): MessageDescriptor {
  return errorKind(e) === "network" ? msg("settings.offline") : msg("settings.failed");
}

export class AppPasswordsViewModel extends ViewModel<AppPasswordsState> {
  private readonly deps: SettingsDeps;
  readonly ws: WorkspaceViewModel;

  constructor(ws: WorkspaceViewModel, deps: SettingsDeps) {
    super({ status: "loading", passwords: [], atLimit: false, nearLimit: false, revoking: false });
    this.ws = ws;
    this.deps = deps;
  }

  private setPasswords(passwords: AppPassword[]): void {
    this.update({ passwords, atLimit: passwords.length >= APP_PASSWORD_LIMIT, nearLimit: passwords.length >= APP_PASSWORD_NEAR_LIMIT });
  }

  async load(): Promise<void> {
    try {
      const list = await this.deps.api.appPasswords(this.ws.wsId);
      if (this.isDisposed) return;
      this.setPasswords(list);
      this.update({ status: "ready" });
    } catch {
      if (!this.isDisposed && this.state.status !== "ready") this.update({ status: "error" });
    }
  }

  /** Not used for a long while (or never, long after it was made): the row suggests revoking it. */
  isStale(p: AppPassword): boolean {
    const now = this.ws.deps.clock.now();
    return now - (p.usedAt ?? p.createdAt) > APP_PASSWORD_STALE_MS;
  }

  /** Opens the add sheet for a setup, its name prefilled (`defaultName`, from the platform's strings). */
  startAdd(setup: AppPasswordSetup, defaultName: string): void {
    if (this.state.atLimit) return;
    this.update({ adding: { setup, name: defaultName, busy: false } });
  }

  setName(name: string): void {
    const a = this.state.adding;
    if (a !== undefined && a.made === undefined) this.update({ adding: { ...a, name } });
  }

  get canCreate(): boolean {
    const a = this.state.adding;
    return a !== undefined && !a.busy && a.made === undefined && a.name.trim().length > 0 && a.name.trim().length <= 80;
  }

  cancelAdd(): void {
    if (this.state.adding?.busy !== true) this.update({ adding: undefined });
  }

  /**
   * Makes the password. Mail and calendar: the sheet then shows it once. Profile: resolves with the file for the
   * platform to download, and the sheet closes.
   */
  async create(): Promise<AppleProfileFile | undefined> {
    const a = this.state.adding;
    if (a === undefined || !this.canCreate) return undefined;
    const label = a.name.trim();
    this.update({ adding: { ...a, busy: true } });
    try {
      if (a.setup === "profile") {
        const file = await this.deps.api.createAppleProfile(this.ws.wsId, label);
        if (this.isDisposed) return undefined;
        this.update({ adding: undefined, notice: msg("appPasswords.profileDownloaded") });
        void this.load();
        return file;
      }
      const made = await this.deps.api.createAppPassword(this.ws.wsId, { label, purpose: a.setup });
      if (this.isDisposed) return undefined;
      this.setPasswords([made.appPassword, ...this.state.passwords.filter((p) => p.id !== made.appPassword.id)]);
      this.update({ adding: { ...a, busy: false, made: { password: made.password, username: made.username, server: made.server, label } } });
      return undefined;
    } catch (e) {
      if (this.isDisposed) return undefined;
      this.update({ adding: { ...a, busy: false }, notice: failure(e) });
      // At the limit (another device added one meanwhile): the list shows why.
      void this.load();
      return undefined;
    }
  }

  /** Closes the "shown once" step; the password is gone from memory. */
  finishAdd(): void {
    if (this.state.adding?.made !== undefined) this.update({ adding: undefined });
  }

  askRevoke(id: string): void {
    const p = this.state.passwords.find((x) => x.id === id);
    if (p !== undefined && !this.state.revoking) this.update({ confirming: { kind: "one", id, label: p.label } });
  }

  askRevokeAll(): void {
    if (this.state.passwords.length > 0 && !this.state.revoking) this.update({ confirming: { kind: "all", count: this.state.passwords.length } });
  }

  cancelRevoke(): void {
    if (!this.state.revoking) this.update({ confirming: undefined });
  }

  async confirmRevoke(): Promise<void> {
    const c = this.state.confirming;
    if (c === undefined || this.state.revoking) return;
    this.update({ revoking: true });
    try {
      if (c.kind === "one") await this.deps.api.revokeAppPassword(this.ws.wsId, c.id);
      else await this.deps.api.revokeAllAppPasswords(this.ws.wsId);
      if (this.isDisposed) return;
      this.done(c);
    } catch (e) {
      if (this.isDisposed) return;
      // Already gone (revoked elsewhere): the result is the same.
      if (c.kind === "one" && errorKind(e) === "notFound") this.done(c);
      else this.update({ notice: failure(e) });
    } finally {
      if (!this.isDisposed) this.update({ revoking: false });
    }
  }

  private done(c: NonNullable<AppPasswordsState["confirming"]>): void {
    if (c.kind === "one") {
      this.setPasswords(this.state.passwords.filter((p) => p.id !== c.id));
      this.update({ confirming: undefined, notice: c.label === null ? msg("appPasswords.revokedUnnamed") : msg("appPasswords.revokedOne", { name: c.label }) });
    } else {
      this.setPasswords([]);
      this.update({ confirming: undefined, notice: msg("appPasswords.revokedAll", { count: c.count }) });
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
